import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Divider,
  FormControlLabel,
  Switch,
  Alert,
  Chip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import { useQuery, useMutation } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import { useToast } from '../../contexts/ToastContext';

type TvOtpResponse = {
  tvDisplayOtp?: string;
  tvDisplayOtpGeneratedAt?: string;
  tvDisplayOtpExpiresAt?: string;
  tvDisplayOtpAutoGenerateEnabled?: boolean;
  tvDisplayOtpAutoGenerateTime?: string;
  tvDisplayOtpExpiryEnabled?: boolean;
  tvDisplayOtpExpiryDays?: number;
  expired?: boolean;
};

const TvDisplayOtpSection: React.FC<{ schoolId: string }> = ({ schoolId }) => {
  const { showToast } = useToast();

  const { data: tvOtpData, refetch: refetchTvOtp } = useQuery<TvOtpResponse>(
    ['tv-display-otp', schoolId],
    async (): Promise<TvOtpResponse> => {
      const res = await apiClient.get(`/schools/${schoolId}/tv-display-otp`);
      return res.data as TvOtpResponse;
    },
    { enabled: !!schoolId }
  );
  const [tvOtpAutoGenEnabled, setTvOtpAutoGenEnabled] = useState(false);
  const [tvOtpAutoGenTime, setTvOtpAutoGenTime] = useState('00:00');
  const [tvOtpExpiryEnabled, setTvOtpExpiryEnabled] = useState(false);
  const [tvOtpExpiryDays, setTvOtpExpiryDays] = useState(1);

  useEffect(() => {
    if (tvOtpData) {
      setTvOtpAutoGenEnabled(!!tvOtpData.tvDisplayOtpAutoGenerateEnabled);
      setTvOtpAutoGenTime(tvOtpData.tvDisplayOtpAutoGenerateTime || '00:00');
      setTvOtpExpiryEnabled(!!tvOtpData.tvDisplayOtpExpiryEnabled);
      setTvOtpExpiryDays(tvOtpData.tvDisplayOtpExpiryDays || 1);
    }
  }, [tvOtpData]);

  const generateTvOtpMutation = useMutation(
    async () => {
      const res = await apiClient.post(`/schools/${schoolId}/tv-display-otp`, {
        tvDisplayOtpAutoGenerateEnabled: tvOtpAutoGenEnabled,
        tvDisplayOtpAutoGenerateTime: tvOtpAutoGenTime,
        tvDisplayOtpExpiryEnabled: tvOtpExpiryEnabled,
        tvDisplayOtpExpiryDays: tvOtpExpiryDays,
      });
      return res.data;
    },
    {
      onSuccess: () => {
        refetchTvOtp();
        showToast('success', 'TV display OTP regenerated. All connected displays will need to re-enter the new OTP.');
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to generate OTP');
      },
    }
  );
  const saveTvOtpSettingsMutation = useMutation(
    async () => {
      const res = await apiClient.put(`/schools/${schoolId}/tv-display-otp`, {
        tvDisplayOtpAutoGenerateEnabled: tvOtpAutoGenEnabled,
        tvDisplayOtpAutoGenerateTime: tvOtpAutoGenTime,
        tvDisplayOtpExpiryEnabled: tvOtpExpiryEnabled,
        tvDisplayOtpExpiryDays: tvOtpExpiryDays,
      });
      return res.data;
    },
    {
      onSuccess: () => {
        refetchTvOtp();
        showToast('success', 'TV display OTP settings saved.');
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to save OTP settings');
      },
    }
  );

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" gutterBottom>TV Display OTP</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        The TV display requires a <strong>school code + OTP</strong> to log in. Share the OTP with whoever sets up the display.
        By default the OTP <strong>expires at midnight</strong> and a new one is auto-generated — the display will ask for the new OTP.
        You can also regenerate the OTP manually at any time to immediately log out connected displays.
      </Alert>

      {tvOtpData?.tvDisplayOtp ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Chip
            label={tvOtpData.tvDisplayOtp}
            sx={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '0.25em', py: 2.5, px: 1 }}
            variant="outlined"
            color={tvOtpData.expired ? 'error' : 'primary'}
          />
          {tvOtpData.expired && <Chip label="Expired" color="error" size="small" />}
          <Button
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={() => { navigator.clipboard.writeText(tvOtpData.tvDisplayOtp!); showToast('success', 'OTP copied to clipboard'); }}
          >
            Copy
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<RefreshIcon />}
            onClick={() => { if (!generateTvOtpMutation.isLoading) generateTvOtpMutation.mutate(); }}
            disabled={generateTvOtpMutation.isLoading}
          >
            {generateTvOtpMutation.isLoading ? 'Regenerating…' : 'Regenerate OTP'}
          </Button>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {tvOtpData.tvDisplayOtpGeneratedAt && (
              <Typography variant="caption" color="text.secondary">
                Generated: {new Date(tvOtpData.tvDisplayOtpGeneratedAt).toLocaleString()}
              </Typography>
            )}
            {tvOtpData.tvDisplayOtpExpiresAt && (
              <Typography variant="caption" color="text.secondary">
                Expires: {new Date(tvOtpData.tvDisplayOtpExpiresAt).toLocaleString()}
              </Typography>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Alert severity="warning" sx={{ flex: 1 }}>No OTP generated yet. Generate one to secure your TV display login.</Alert>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => { if (!generateTvOtpMutation.isLoading) generateTvOtpMutation.mutate(); }}
            disabled={generateTvOtpMutation.isLoading}
          >
            {generateTvOtpMutation.isLoading ? 'Generating…' : 'Generate OTP'}
          </Button>
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <FormControlLabel
          control={<Switch checked={tvOtpAutoGenEnabled} onChange={(e) => setTvOtpAutoGenEnabled(e.target.checked)} />}
          label={<Typography variant="subtitle2">Custom daily auto-generation time</Typography>}
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 6, mb: tvOtpAutoGenEnabled ? 1.5 : 0 }}>
          {tvOtpAutoGenEnabled
            ? 'A new OTP will be generated automatically at the time below each day. Connected displays will be logged out and need the new OTP.'
            : 'Disabled — OTP auto-generates at midnight (00:00) each day.'}
        </Typography>
        {tvOtpAutoGenEnabled && (
          <TextField
            size="small"
            type="time"
            label="Auto-generate time"
            value={tvOtpAutoGenTime}
            onChange={(e) => setTvOtpAutoGenTime(e.target.value || '00:00')}
            sx={{ ml: 6, width: 160 }}
            helperText="e.g. 06:00 = generate new OTP at 6 AM"
          />
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <FormControlLabel
          control={<Switch checked={tvOtpExpiryEnabled} onChange={(e) => setTvOtpExpiryEnabled(e.target.checked)} />}
          label={<Typography variant="subtitle2">Extended OTP validity (multi-day)</Typography>}
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 6, mb: tvOtpExpiryEnabled ? 1.5 : 0 }}>
          {tvOtpExpiryEnabled
            ? 'OTP will stay valid for the number of days below from when it was generated. Useful if you don\'t want to change the OTP every day.'
            : 'Disabled — OTP expires at the next auto-generation time (midnight by default). A new OTP is generated automatically.'}
        </Typography>
        {tvOtpExpiryEnabled && (
          <TextField
            size="small"
            type="number"
            label="Valid for (days)"
            value={tvOtpExpiryDays}
            onChange={(e) => setTvOtpExpiryDays(Math.max(1, Number(e.target.value) || 1))}
            inputProps={{ min: 1, max: 365 }}
            sx={{ ml: 6, width: 140 }}
            helperText="e.g. 7 = OTP works for a week"
          />
        )}
      </Paper>

      <Button
        size="small"
        variant="outlined"
        startIcon={<SaveIcon />}
        onClick={() => { if (!saveTvOtpSettingsMutation.isLoading) saveTvOtpSettingsMutation.mutate(); }}
        disabled={saveTvOtpSettingsMutation.isLoading}
      >
        {saveTvOtpSettingsMutation.isLoading ? 'Saving…' : 'Save OTP settings'}
      </Button>
    </>
  );
};

export default TvDisplayOtpSection;
